import { globals } from 'config/globals';
import HomeColours from 'app/views/home/config/HomeColours';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PATH_ORBIT,
	BEHAVIOUR_PROGRESS_PARALLAX,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	PATH_FLAG_SCALE_ACTIVE,
	PATH_FLAG_SYNC_ROTATION,
	REPELL_HAS_RETURN,
	REPELL_POSITION,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { LOOKUP_CIRCLE } from 'app/views/home/webgl/particlesOptimised/core/pathLookups';
import { FRAME_SOLID2, FRAME_SQUARE, FRAME_TRI } from 'app/views/home/webgl/particlesOptimised/core/frames';
import { hexToTint } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import { GROUP_SCREENS } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensContext';

import type { ScreensContext } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * SwirlPhase — the reference ScreensDepartment's 'swirl' section (_doSwirl +
 * ScreensParticleProcessor.swirl/_orbitRepell), phase-file'd in the screens
 * retrofit. BODIES are the verbatim stage-5 lane ports that lived in
 * particlesOptimised ScreensDepartment._doSwirl/_swirlLane — the retrofit
 * moved them, it did not change them.
 *
 * VISUAL: 24 concentric rings of graduated-colour dots/triangles bloom from a
 * tiny disc, contract onto a slow counter-rotating swirl, widen, then balloon
 * out and shrink to nothing — staged by per-lane ScriptList entries at t
 * 1200/3300/6000ms — while the TVs flick on around it and shut off at 7s.
 *
 * NOTE (reference quirk kept): _doSwirl does NOT clear the shared
 * ComboCounter — its TV beats stack onto dropMachine's leftovers (why the
 * counter is context-owned; see ScreensContext). The stage scripts sit in
 * GROUP_SCREENS: cancelled on stop/park only, all fire inside the phase's 7s.
 */
export class SwirlPhase implements DepartmentPhase {
	public readonly name = 'swirl';

	private _ctx: ScreensContext;

	constructor(ctx: ScreensContext) {
		this._ctx = ctx;
	}

	public init(): void {
		//No one-time setup — the ring/scene tables are per-arm locals
		//(reference _doSwirl builds them per call).
	}

	/** _doSwirl, verbatim. */
	public start(): void {
		const ctx = this._ctx;

		const sceneconfig = [
			{ rscale: 0.16, scale: 0.2, x: 0.0143 + 0.0445, y: 0.31, t: 0 },
			{ rscale: 1, scale: 1, x: -0.04 + 0.034, y: 0.5, t: 1200 },
			{ rscale: 1.05, scale: 1.1, x: -0.04 + 0.034, y: 0.5, t: 3300 },
			{ rscale: 3, scale: 0, x: -0.04 + 0.034, y: 0.5, t: 6000 }
		];

		const perRing = 360;
		const step = 4;

		const settings = [
			{ r: 495, s: 1.2 },
			{ r: 465, s: 1.2 },
			{ r: 435, s: 1.1 },
			{ r: 410, s: 1 },
			{ r: 390, s: 0.95 },
			{ r: 370, s: 0.85 },
			{ r: 350, s: 0.8 },
			{ r: 330, s: 0.8 },
			{ r: 310, s: 0.75 },
			{ r: 290, s: 0.6 },
			{ r: 270, s: 0.5 },
			{ r: 250, s: 0.5 },
			{ r: 230, s: 0.48 },
			{ r: 210, s: 0.45 },
			{ r: 190, s: 0.42 },
			{ r: 170, s: 0.4 },
			{ r: 150, s: 0.35 },
			{ r: 130, s: 0.32 },
			{ r: 110, s: 0.28 },
			{ r: 90, s: 0.24 },
			{ r: 70, s: 0.14 },
			{ r: 50, s: 0.12 },
			{ r: 30, s: 0.11 },
			{ r: 10, s: 0.1 }
		];

		let index = -1;

		//NOTE: the yellow phase REVERSES HomeColours.graduated IN PLACE —
		//shared-array mutation, exactly like the reference:
		let colours = HomeColours.graduated;
		if (globals.colourPhase === 'yellow') colours = colours.reverse();

		for (let i = 0; i < settings.length; i++) {
			const c = colours[i];
			let shape = FRAME_SOLID2;
			const ran = Math.random();
			if (ran < 0.05) {
				shape = FRAME_SQUARE;
			} else if (ran < 0.4) {
				shape = FRAME_TRI;
			}

			for (let j = 0; j < perRing; j += step) {
				index++;
				if (index < ctx.range.count) {
					this._swirlLane(ctx.range.start + index, settings[i], j, c, shape, sceneconfig);
				}
			}
		}

		HomeScreens.allOff();
		ctx.counters.add(() => HomeScreens.on('topRight'), 50);
		ctx.counters.add(() => HomeScreens.on('topLeft'), 1200);
		ctx.counters.add(() => HomeScreens.on('bottomRight'), 1300);
		ctx.counters.add(() => HomeScreens.on('bottomLeft'), 1800);
		ctx.counters.add(() => HomeScreens.allOff(), 7000);

		//Reference off-by-one sweep kept: starts at index - 1 (re-deactivating
		//the last two used particles) and STOPS before the final pool slot
		//(one stale particle survives):
		for (let i = index - 1; i < ctx.range.count - 1; i++) {
			ctx.core.stateFlags[ctx.range.start + i] &= ~FLAG_ACTIVE;
		}
	}

	public tick(): void {
		//All motion is kernel-side (orbit path + POSITION repell); the stage
		//changes run on GROUP_SCREENS scripts, the TV beats on the shared
		//ComboCounter.
	}

	public hide(): void {
		//Pending stage scripts / combo entries carry over by design (see the
		//class header). Safe on a never-started phase.
	}

	public reset(): void {
		//No persistent phase state (the HomeColours.graduated in-place
		//reversal persists ON PURPOSE — shared-array mutation, reference).
	}

	/** ScreensParticleProcessor.swirl, lane-for-line (+ _orbitRepell). */
	private _swirlLane(
		lane: number,
		config: { r: number; s: number },
		circleIndex: number,
		colourHex: string,
		shape: number,
		sceneconfig: Array<{ rscale: number; scale: number; x: number; y: number; t: number }>
	): void {
		const core = this._ctx.core;
		const orbit = core.orbitPath;

		//deactivateAllBehaviours(true):
		core.behaviourMask[lane] =
			(core.behaviourMask[lane] & (BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X)) | BEHAVIOUR_PROGRESS_PARALLAX;

		core.textureId[lane] = shape;

		//activate():
		core.stateFlags[lane] |= FLAG_ACTIVE;

		//orbitPath config:
		core.behaviourMask[lane] |= BEHAVIOUR_PATH_ORBIT;
		orbit.xMul[lane] = config.r * sceneconfig[0].rscale;
		orbit.yMul[lane] = config.r * sceneconfig[0].rscale;
		orbit.speed[lane] = 1;
		orbit.idx[lane] = circleIndex; //pathIndex = the ring angle
		orbit.speedMulTarget[lane] = 1;
		orbit.flags[lane] &= ~PATH_FLAG_SCALE_ACTIVE;
		orbit.lookupId[lane] = LOOKUP_CIRCLE;
		orbit.flags[lane] |= PATH_FLAG_SYNC_ROTATION;

		core.blendAdd[lane] = 0; //sprite.blendMode = null

		core.tAlpha[lane] = core.alpha[lane] = 0.8 + Math.random() * 0.2;

		//setVectorConfig(false) x2:
		writeVectorConfig(core, lane, VEC_X, false);
		writeVectorConfig(core, lane, VEC_Y, false);

		//stopBreathing():
		core.behaviourMask[lane] &= ~BEHAVIOUR_BREATHING;

		const shapeFix = shape == FRAME_SQUARE ? 0.75 : 1;
		const baseScale = config.s * sceneconfig[0].scale * shapeFix;
		//hardSetScale(baseScale) then scale = 0:
		core.tScale[lane] = baseScale;
		core.tScaleSaved[lane] = baseScale;
		core.scale[lane] = 0;

		core.tint[lane] = hexToTint(colourHex);

		//setEase({x: 20*s, y: 20*s, scale: 20, alpha: 2}):
		core.easeX[lane] = 20 * config.s;
		core.easeY[lane] = 20 * config.s;
		core.easeScale[lane] = 20;
		core.easeAlpha[lane] = 2;

		//position.set(sceneconfig[0], true):
		this._ctx.setHardPosition(lane, sceneconfig[0].x, sceneconfig[0].y);

		//_orbitRepell(): POSITION, radius 200, randomisePower(0.001, 0),
		//returnTo {-0.04, 0.5}...
		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		core.repellType[lane] = REPELL_POSITION;
		core.repellRadius[lane] = 200;
		core.repellPower[lane] = 0.001 + Math.random() * 0;
		core.repellFlags[lane] |= REPELL_HAS_RETURN;
		core.repellReturnX[lane] = -0.04;
		core.repellReturnY[lane] = 0.5;

		//...immediately retargeted to the stage-0 centre:
		core.repellReturnX[lane] = sceneconfig[0].x;
		core.repellReturnY[lane] = sceneconfig[0].y;

		//Stage 1 (t 1200): contract onto the swirl, reverse, breathe:
		this._ctx.deps.scripts.addMs(
			lane,
			sceneconfig[1].t,
			(l: number) => {
				orbit.xMul[l] = config.r * sceneconfig[1].rscale;
				orbit.yMul[l] = config.r * sceneconfig[1].rscale;
				orbit.dir[l] = -1;
				orbit.speedMulTarget[l] = 0.25;
				core.tScale[l] = core.tScaleSaved[l] = config.s * sceneconfig[1].scale * shapeFix;

				writeVectorConfig(core, l, VEC_X, false);
				writeVectorConfig(core, l, VEC_Y, false);

				core.relX[l] = sceneconfig[1].x;
				core.relY[l] = sceneconfig[1].y;

				core.repellReturnX[l] = sceneconfig[1].x;
				core.repellReturnY[l] = sceneconfig[1].y;

				core.easeX[l] = 30 * config.s;
				core.easeY[l] = 30 * config.s;
				core.easeScale[l] = 20;
				core.easeAlpha[l] = 2;

				//startBreathing(baseScale, 0.01, 0.5):
				core.breathBase[l] = baseScale;
				core.breathChange[l] = 0.01;
				core.breathMax[l] = 0.5;
				core.breathAdd[l] = 0;
				core.breathDir[l] = 1;
				core.behaviourMask[l] |= BEHAVIOUR_BREATHING;
			},
			false,
			GROUP_SCREENS
		);

		//Stage 2 (t 3300): widen slightly, forward direction:
		this._ctx.deps.scripts.addMs(
			lane,
			sceneconfig[2].t,
			(l: number) => {
				orbit.xMul[l] = config.r * sceneconfig[2].rscale;
				orbit.yMul[l] = config.r * sceneconfig[2].rscale;
				orbit.dir[l] = 1;
				core.tScale[l] = core.tScaleSaved[l] = config.s * sceneconfig[2].scale;

				core.relX[l] = sceneconfig[2].x;
				core.relY[l] = sceneconfig[2].y;

				core.repellReturnX[l] = sceneconfig[2].x;
				core.repellReturnY[l] = sceneconfig[2].y;
			},
			false,
			GROUP_SCREENS
		);

		//Stage 3 (t 6000): balloon out and shrink to nothing:
		this._ctx.deps.scripts.addMs(
			lane,
			sceneconfig[3].t,
			(l: number) => {
				orbit.xMul[l] = config.r * sceneconfig[3].rscale;
				orbit.yMul[l] = config.r * sceneconfig[3].rscale;
				orbit.dir[l] = -1;
				core.tScale[l] = core.tScaleSaved[l] = config.s * sceneconfig[3].scale;

				core.relX[l] = sceneconfig[3].x;
				core.relY[l] = sceneconfig[3].y;

				core.repellReturnX[l] = sceneconfig[3].x;
				core.repellReturnY[l] = sceneconfig[3].y;

				core.easeX[l] = 30;
				core.easeY[l] = 30;
				core.easeScale[l] = 30;
				core.easeAlpha[l] = 2;
			},
			false,
			GROUP_SCREENS
		);
	}
}
